import React, { useState, useEffect, useCallback, useRef } from 'react'
import axios from 'axios'
import { useAuth } from 'react-oidc-context'
import HomeTab from './components/HomeTab'
import UploadTab from './components/UploadTab'
import RulesTab from './components/RulesTab'
import PromptBuilder from './components/PromptBuilder'
import JobDetails from './pages/JobDetails'
import './App.css'
import jabilLogo from './asset/logo-jabil.svg'
import InlineLogo from './components/InlineLogo'
import { getAssetUrl } from './utils/asset'
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom'
import SignInPage from './pages/SignInPage'
import AccessDeniedPage from './pages/AccessDeniedPage'
import SessionExpiredPage from './pages/SessionExpiredPage'
import AuthLoadingPage from './pages/AuthLoadingPage'

interface ChartData {
  name: string
  value: number
  [key: string]: string | number
}

interface TableData {
  id: number
  name: string
  status: string
  value: number
}

interface ApiStats {
  total_parts_analyzed: number
  category_totals: {
    IGNORE: number
    CRITICAL: number
    CONSTRAINT: number
  }
  monthly_trend: Array<{
    month: string
    CRITICAL: number
    CONSTRAINT: number
    IGNORE: number
    parts: number
  }>
  recent_analyses: Array<{
    job_id: string
    completed_at: string
    input_filename: string
    parts_analyzed: number
    critical: number
    constraint: number
    ignore: number
  }>
}

// Debug: log the imported asset so we can see how Parcel resolves it at runtime
console.debug('jabilLogo import value:', jabilLogo)

const COGNITO_CLIENT_ID = '4v70pq3q7efhd63dlh90h62ejd'
const COGNITO_LOGOUT_URI = 'https://mcp-frontend.aws-practice.royalcyber.org/'
const COGNITO_DOMAIN = 'https://us-east-1kuxzrpgfn.auth.us-east-1.amazoncognito.com'

const App: React.FC = () => {
  const auth = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const hideTabs = location?.pathname?.startsWith?.('/job/') || false

  const signOutRedirect = () => {
    window.location.href = `${COGNITO_DOMAIN}/logout?client_id=${COGNITO_CLIENT_ID}&logout_uri=${encodeURIComponent(COGNITO_LOGOUT_URI)}`
  }

  const handleAuthRetry = () => {
    auth.removeUser()
    auth.signinRedirect()
  }

  // When the IdP redirects back with error in the URL (e.g. user declined consent / "Back to App"),
  // redirect to a clean error page so we don't show the raw query string.
  useEffect(() => {
    const search = location.search
    if (!search || location.pathname === '/access-denied' || location.pathname === '/session-expired') return
    const params = new URLSearchParams(search)
    const error = params.get('error')
    const errorDesc = params.get('error_description') || ''
    if (!error) return
    const isExpired = /expired|login_required|invalid_token/i.test(error) || /expired|login_required|session/i.test(errorDesc)
    if (isExpired) {
      navigate('/session-expired', { replace: true })
    } else {
      navigate('/access-denied', { replace: true })
    }
  }, [location.search, location.pathname, navigate])

  // When OIDC context reports auth.error, sync URL to the right auth page
  useEffect(() => {
    if (!auth.error || auth.isAuthenticated) return
    const path = location.pathname
    const isExpired =
      /expired|login_required|session|invalid_token/i.test(auth.error.message || '') ||
      (auth.error as { error?: string })?.error === 'login_required'
    if (isExpired && path !== '/session-expired') {
      navigate('/session-expired', { replace: true })
    } else if (!isExpired && path !== '/access-denied') {
      navigate('/access-denied', { replace: true })
    }
  }, [auth.error, auth.isAuthenticated, location.pathname, navigate])

  // After successful callback, clear code/state from URL so a refresh restores session from storage
  // instead of re-using the one-time code (which would fail and show Access Denied).
  useEffect(() => {
    if (!auth.isAuthenticated || !location.search) return
    const params = new URLSearchParams(location.search)
    if (params.has('code') || params.has('state')) {
      navigate(location.pathname, { replace: true })
    }
  }, [auth.isAuthenticated, location.search, location.pathname, navigate])

  const [activeTab, setActiveTab] = useState<'home' | 'upload' | 'rules' | 'prompt'>('home')
  const [jobToken, setJobToken] = useState<string>('')
  const [jobStatus, setJobStatus] = useState<string>('')
  const [jobProgress, setJobProgress] = useState<number>(0)
  const [jobTotalRows, setJobTotalRows] = useState<number>(0)
  const [stats, setStats] = useState<ApiStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [chartData, setChartData] = useState<ChartData[]>([])
  const [pieData, setPieData] = useState<Array<{ name: string; value: number }>>([])
  const [tableData, setTableData] = useState<TableData[]>([])

  // Fetch stats from API
  const fetchStats = useCallback(async () => {
    try {
      setLoading(true)
      const response = await axios.get('/jobs/stats', {
        headers: {
          'Accept': 'application/json'
        }
      })
      console.log('Stats response:', response)
      const apiStats = response.data

      setStats(apiStats)

      // Transform monthly_trend data for bar chart
      const transformedChartData = apiStats.monthly_trend.map((item: any) => ({
        name: item.month,
        value: item.parts,
        CRITICAL: item.CRITICAL,
        CONSTRAINT: item.CONSTRAINT,
        IGNORE: item.IGNORE,
      }))
      setChartData(transformedChartData)

      // Transform category_totals for pie chart
      const pieChartData = [
        { name: 'Critical', value: apiStats.category_totals.CRITICAL },
        { name: 'Constraint', value: apiStats.category_totals.CONSTRAINT },
        { name: 'Ignore', value: apiStats.category_totals.IGNORE },
      ]
      setPieData(pieChartData)

      // Transform recent_analyses for table
      const transformedTableData = apiStats.recent_analyses.map((analysis: any, index: number) => ({
        id: index + 1,
        name: analysis.input_filename,
        status: 'Completed',
        value: analysis.parts_analyzed,
        jobId: analysis.job_id,
        date: new Date(analysis.completed_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
        }),
        critical: analysis.critical,
        constraint: analysis.constraint,
        ignore: analysis.ignore,
      }))
      setTableData(transformedTableData as any)
    } catch (error) {
      console.warn('Failed to fetch stats:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  // Fetch stats on component mount
  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  // Handle file upload
  const handleFileUpload = useCallback(async (file: File, config?: any) => {
    try {
      const formData = new FormData()
      formData.append('file', file)

      // Add config to request: part_limit + email (enabled, recipient_emails)
      const configObj = config || {
        part_limit: 500,
        email: { enabled: false, recipient_emails: [] }
      }
      formData.append('config', JSON.stringify(configObj))

      const response = await axios.post('/jobs', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      const jobId = response.data.job_id
      setJobToken(jobId)
      return jobId
    } catch (error) {
      console.warn('Upload failed:', error)
      throw error
    }
  }, [])

  // Poll job status (log polling errors at most once per job to avoid console/overlay spam)
  const pollErrorLoggedRef = useRef(false)
  useEffect(() => {
    if (!jobToken || activeTab !== 'upload') return
    pollErrorLoggedRef.current = false

    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`/jobs/${jobToken}`)
        const jobData = response.data

        setJobStatus(jobData.status)
        setJobProgress(typeof jobData.progress === 'number' ? jobData.progress : 0)
        setJobTotalRows(typeof jobData.total_rows === 'number' ? jobData.total_rows : 0)

        if (jobData.status === 'COMPLETED') {
          clearInterval(pollInterval)
          fetchStats()
          // Stay on Run Job tab; user clicks "Show details" to go to job detail page
        }
      } catch (error) {
        if (!pollErrorLoggedRef.current) {
          pollErrorLoggedRef.current = true
          console.warn('Job polling failed (backend may be unreachable):', error)
        }
      }
    }, 3000) // Poll every 3 seconds

    return () => clearInterval(pollInterval)
  }, [jobToken, activeTab, fetchStats])

  // Render logo helper to avoid IIFE in JSX which can confuse some parsers
  const renderLogo = () => {
    const logoSrc = getAssetUrl(jabilLogo)
    if (logoSrc) {
      return (
        <img
          src={logoSrc}
          alt="Jabil Logo"
          className="logo"
          onError={(e) => console.error('Failed to load logo:', logoSrc, e)}
        />
      )
    }
    return <InlineLogo className="logo" />
  }

  // Auth state: loading
  if (auth.isLoading) {
    return <AuthLoadingPage />
  }

  // Auth state: not authenticated — show sign-in, access-denied, or session-expired based on route / error
  if (!auth.isAuthenticated) {
    if (location.pathname === '/access-denied') {
      return <AccessDeniedPage onRetry={handleAuthRetry} />
    }
    if (location.pathname === '/session-expired') {
      return <SessionExpiredPage onSignInAgain={handleAuthRetry} />
    }
    if (auth.error) {
      const isExpired =
        /expired|login_required|session|invalid_token/i.test(auth.error.message || '') ||
        (auth.error as { error?: string })?.error === 'login_required'
      if (isExpired) return <SessionExpiredPage onSignInAgain={handleAuthRetry} />
      return <AccessDeniedPage onRetry={handleAuthRetry} />
    }
    return <SignInPage onSignIn={() => auth.signinRedirect()} />
  }

  return (
    <div className="app-container">
      {/* Main Content */}
      <div className="main-content">
        {/* Header */}
        <div className="header">
          {/* Ensure compatibility with different bundlers: Parcel may export assets as URL string or module with default export */}
          <div className="logo-icon">
            {/* {renderLogo()} 
            <div className="slide-icon">
              <svg width="50" height="78" viewBox="0 0 50 78" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M45.0333 0L0 78H4.19028L49.2236 0H45.0333Z" fill="url(#paint0_linear_3194_4503)"></path>
                <defs>
                  <linearGradient id="paint0_linear_3194_4503" x1="5.86143" y1="78" x2="5.86162" y2="5.48426" gradientUnits="userSpaceOnUse">
                    <stop offset="0.0677083" stopColor="#15BEF0" stopOpacity="0" />
                    <stop offset="0.390625" stopColor="#15BEF0" />
                    <stop offset="0.625" stopColor="#7FBA00" />
                    <stop offset="0.932292" stopColor="#7FBA00" stopOpacity="0" />
                  </linearGradient>
                </defs>
              </svg>

            </div> */}
          </div>
          <div className="app-title">
            <h2>
              Agentic AI for True Shortage Validation
            </h2>
          </div>
          {auth.isAuthenticated && (
            <button
              className="tab-button"
              onClick={() => auth.removeUser()}
              style={{ marginLeft: 'auto' }}
            >
              Sign out
            </button>
          )}
        </div>

        {/* Tabs (hidden on Job Details) */}
        {!hideTabs && (
          <div className="tabs">
            <button
              className={`tab-button ${activeTab === 'home' ? 'active' : ''}`}
              onClick={() => setActiveTab('home')}
            >
              Home
            </button>
            <button
              className={`tab-button ${activeTab === 'upload' ? 'active' : ''}`}
              onClick={() => setActiveTab('upload')}
            >
              Run Job
            </button>
            <button
              className={`tab-button ${activeTab === 'rules' ? 'active' : ''}`}
              onClick={() => setActiveTab('rules')}
            >
              Rules
            </button>
            <button
              className={`tab-button ${activeTab === 'prompt' ? 'active' : ''}`}
              onClick={() => setActiveTab('prompt')}
            >
              Agent Instructions
            </button>
          </div>
        )}

        {/* Tab Content (supports job details route) */}
        <div className="tab-content">
          <Routes>
            <Route path="/" element={<div>
                {activeTab === 'home' && (
                  <HomeTab
                    chartData={chartData}
                    pieData={pieData}
                    tableData={tableData}
                    stats={stats}
                    loading={loading}
                  />
                )}
                {activeTab === 'upload' && (
                  <UploadTab
                    onFileUpload={handleFileUpload}
                    onTokenReceived={setJobToken}
                    onTabChange={() => setActiveTab('home')}
                    jobToken={jobToken}
                    jobStatus={jobStatus}
                    jobProgress={jobProgress}
                    jobTotalRows={jobTotalRows}
                  />
                )}
                {activeTab === 'rules' && (
                  <RulesTab />
                )}
                {activeTab === 'prompt' && (
                  <PromptBuilder />
                )}
              </div>} />

            <Route path="/job/:jobId" element={<JobDetails />} />
          </Routes>
        </div>
      </div>
    </div>
  )
}

export default App
